// components/ui/StyledQRCode.tsx
'use client';

import { useEffect, useImperativeHandle, useRef, forwardRef, memo } from 'react';
import QRCodeStyling from 'qr-code-styling-ba';
import { getQRStyle } from '@/utils/qrStyles';
import { buildQrConfig } from '@/lib/qr-config';

interface StyledQRCodeProps {
  url: string;
  size?: number;
  styleKey?: string;
  image?: string;
  backgroundOptions?: Record<string, unknown>;
  embedLogoUrl?: string;
  tintElements?: string;
  tintText?: string;
  tintBackground?: string;
}

export interface StyledQRCodeHandle {
  download: (fileName?: string, format?: 'svg' | 'png') => void;
  isReady: () => boolean;
  getCompositeSvgText: () => Promise<string>;
  getCompositePngBlob: (options?: { scale?: number }) => Promise<Blob>;
}

const StyledQRCode = forwardRef<StyledQRCodeHandle, StyledQRCodeProps>(function StyledQRCode(
  { url, size = 150, styleKey = 'default', backgroundOptions = {}, image, embedLogoUrl, tintElements, tintText, tintBackground },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const qrCode = useRef<QRCodeStyling | null>(null);
  const isReadyRef = useRef(false);

  useEffect(() => {
    // Config comes from the canonical builder so the browser and the
    // server-side renderer in sigalmark-be produce byte-identical SVG.
    const baseConfig = getQRStyle(styleKey, image);
    const finalConfig = {
      ...buildQrConfig(
        { url, size, styleKey, image, tintElements, tintText, tintBackground },
        baseConfig as never,
      ),
      backgroundOptions: {
        ...(buildQrConfig(
          { url, size, styleKey, image, tintElements, tintText, tintBackground },
          baseConfig as never,
        ).backgroundOptions as Record<string, unknown>),
        // caller overrides still win
        ...(backgroundOptions as Record<string, unknown>),
      },
    };

    qrCode.current = new QRCodeStyling(finalConfig);

    const target = containerRef.current;
    if (target) {
      qrCode.current.append(target);
    }

    qrCode.current._canvasDrawingPromise?.then(() => {
      isReadyRef.current = true;
    });

    return () => {
      if (target) {
        target.innerHTML = '';
      }
    };
  }, [url, size, styleKey, image, backgroundOptions, tintElements, tintText, tintBackground]);

  useImperativeHandle(ref, () => ({
    async getCompositeSvgText() {
      if (!isReadyRef.current) {
        await qrCode.current?._canvasDrawingPromise;
      }

      const logoBase64 = embedLogoUrl ? await toBase64(embedLogoUrl) : null;

      const svgBlob = (await qrCode.current?.getRawData('svg')) as Blob;
      if (!svgBlob) return '';

      const svgText = await svgBlob.text();
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
      const svgSerializer = new XMLSerializer();
      const qrSVG = svgDoc.documentElement;

      const qrSize = parseInt(qrSVG.getAttribute('width') || size.toString());
      const qrHeight = parseInt(qrSVG.getAttribute('height') || qrSize.toString());
      const logoTargetHeight = Math.round((2 / 3) * qrHeight);

      let logoWidth = 0;

      if (embedLogoUrl) {
        const img = new Image();
        img.src = embedLogoUrl;

        await new Promise<void>((resolve, reject) => {
          img.onload = () => {
            const aspectRatio = img.naturalWidth / img.naturalHeight;
            logoWidth = logoTargetHeight * aspectRatio;
            resolve();
          };
          img.onerror = reject;
        });
      }

      const totalWidth = Math.round(logoWidth + qrSize);

      const combinedSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      combinedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      combinedSvg.setAttribute('width', `${totalWidth}`);
      combinedSvg.setAttribute('height', `${qrHeight}`);
      combinedSvg.setAttribute('viewBox', `0 0 ${totalWidth} ${qrHeight}`);

      if (logoBase64 && logoWidth > 0) {
        const logo = document.createElementNS('http://www.w3.org/2000/svg', 'image');
        logo.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', logoBase64);
        logo.setAttribute('x', '0');
        logo.setAttribute('y', `${(qrHeight - logoTargetHeight) / 2}`);
        logo.setAttribute('width', `${logoWidth}`);
        logo.setAttribute('height', `${logoTargetHeight}`);
        combinedSvg.appendChild(logo);
      }

      const qrGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      qrGroup.setAttribute('transform', `translate(${logoWidth}, 0)`);
      qrGroup.innerHTML = svgSerializer.serializeToString(qrSVG);
      combinedSvg.appendChild(qrGroup);

      return svgSerializer.serializeToString(combinedSvg);
    },
    async getCompositePngBlob(options?: { scale?: number }) {
      const scale = Math.max(1, Math.min(6, options?.scale ?? 2));
      const svgText = await this.getCompositeSvgText();
      if (!svgText) {
        return new Blob([], { type: 'image/png' });
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, 'image/svg+xml');
      const svgElement = doc.documentElement;
      const width = parseInt(svgElement.getAttribute('width') || size.toString());
      const height = parseInt(svgElement.getAttribute('height') || size.toString());

      const canvas = document.createElement('canvas');
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return new Blob([], { type: 'image/png' });
      }

      const img = new Image();
      const dataUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgText)));
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = dataUrl;
      });

      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);

      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((result) => resolve(result ?? new Blob([], { type: 'image/png' })), 'image/png');
      });

      return blob;
    },
    async download(fileName = 'qr-code', format: 'svg' | 'png' = 'svg') {
      if (!isReadyRef.current) {
        console.log('QR not ready yet, waiting...');
        await qrCode.current?._canvasDrawingPromise;
        console.log('Canvas now ready, proceeding to download');
      }

      const finalSvgText = await this.getCompositeSvgText();
      if (!finalSvgText) return;

      if (format === 'svg') {
        const blob = new Blob([finalSvgText], { type: 'image/svg+xml' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${fileName}.svg`;
        a.click();
      } else {
        const blob = await this.getCompositePngBlob({ scale: 4 });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${fileName}.png`;
        a.click();
      }
    },
    isReady() {
      return isReadyRef.current;
    },
  }));

  async function toBase64(url: string): Promise<string> {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  return (
    <div
      ref={containerRef}
      className="w-full h-full" // Temporary border for debugging
    // style={{ aspectRatio: '1 / 1' }}
    />
  );
});

export default memo(StyledQRCode);
